import { Button } from "@/components/ui/button";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import { Separator } from "@/components/ui/separator";
import { cn } from "@/lib/utils";
import { TrendingDown, TrendingUp, Check } from "lucide-react";

interface TierOption {
  tier: 'good' | 'better' | 'best';
  product_id: string;
  product?: {
    name: string;
    brand?: string;
  };
  priceDifference?: number;
}

interface CompactTierSelectorProps {
  currentTier: 'good' | 'better' | 'best';
  availableTiers: TierOption[];
  onTierChange: (tier: 'good' | 'better' | 'best', productId: string) => void;
}

export function CompactTierSelector({ 
  currentTier, 
  availableTiers, 
  onTierChange 
}: CompactTierSelectorProps) {
  const tierLabels = {
    good: 'Good',
    better: 'Better',
    best: 'Best'
  };

  const tierColors = {
    good: 'bg-slate-50 border-slate-300 hover:bg-slate-100 text-slate-900',
    better: 'bg-blue-50 border-blue-300 hover:bg-blue-100 text-blue-900',
    best: 'bg-amber-50 border-amber-300 hover:bg-amber-100 text-amber-900'
  };

  return (
    <TooltipProvider>
      <div className="flex flex-col gap-2 no-print">
        {(['good', 'better', 'best'] as const).map((tier) => {
          const tierData = availableTiers.find(t => t.tier === tier);
          if (!tierData) return null;

          const isSelected = currentTier === tier;
          const priceDiff = tierData.priceDifference || 0;
          const isSavings = priceDiff < 0;
          const isCostIncrease = priceDiff > 0;

          return (
            <Tooltip key={tier}>
              <TooltipTrigger asChild>
                <Button
                  size="sm"
                  variant={isSelected ? "default" : "outline"}
                  className={cn(
                    "flex flex-col items-center justify-center h-auto py-2 px-3 min-w-[90px] transition-all",
                    "font-semibold border-2",
                    isSelected 
                      ? "bg-primary text-primary-foreground shadow-md border-primary" 
                      : tierColors[tier]
                  )}
                  onClick={() => onTierChange(tier, tierData.product_id)}
                >
                  <div className="flex items-center gap-1">
                    <span className="text-xs uppercase tracking-wide">
                      {tierLabels[tier]}
                    </span>
                    {isSelected && <Check className="w-3 h-3" />}
                  </div>
                  
                  {!isSelected && priceDiff !== 0 && (
                    <div className={cn(
                      "flex items-center gap-0.5 text-[10px] font-bold mt-0.5",
                      isSavings ? "text-green-600" : "text-amber-600"
                    )}>
                      {isSavings && <TrendingDown className="w-3 h-3" />}
                      {isCostIncrease && <TrendingUp className="w-3 h-3" />}
                      <span>
                        {isSavings ? 'Save ' : '+'}
                        ${Math.abs(priceDiff).toLocaleString('en-US', { 
                          minimumFractionDigits: 0, 
                          maximumFractionDigits: 0 
                        })}
                      </span>
                    </div>
                  )}
                  
                  {isSelected && (
                    <span className="text-[9px] uppercase tracking-wider opacity-90 mt-0.5">
                      Selected
                    </span>
                  )}
                </Button>
              </TooltipTrigger>
              
              <TooltipContent side="left" className="max-w-xs">
                <div className="space-y-2">
                  <p className="font-bold text-sm">{tierLabels[tier]} Tier</p>
                  <p className="text-xs font-medium">{tierData.product?.name}</p>
                  {tierData.product?.brand && (
                    <p className="text-xs text-muted-foreground">{tierData.product.brand}</p>
                  )}
                  
                  <Separator />
                  
                  {!isSelected && priceDiff !== 0 && (
                    <div className={cn(
                      "text-sm font-bold p-2 rounded",
                      isSavings ? "bg-green-50 text-green-700" : "bg-amber-50 text-amber-700"
                    )}>
                      {isSavings ? (
                        <>Save ${Math.abs(priceDiff).toLocaleString('en-US', { maximumFractionDigits: 0 })} vs current</>
                      ) : (
                        <>+${Math.abs(priceDiff).toLocaleString('en-US', { maximumFractionDigits: 0 })} upgrade</>
                      )}
                    </div>
                  )}
                  
                  {isSelected && (
                    <p className="text-xs text-primary font-semibold">
                      ✓ Currently selected
                    </p>
                  )}
                </div>
              </TooltipContent>
            </Tooltip>
          );
        })}
      </div>
    </TooltipProvider>
  );
}
